import React, { useMemo, useState } from 'react';
import {
  BoltIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ATTENTION_SEVERITY, formatAttentionMoney } from '../../utils/storeAttention';

const TONES = {
  critical: {
    icon: ExclamationTriangleIcon,
    chip: 'bg-rose-500/10 text-rose-700 border-rose-200',
    chipDark: 'bg-rose-500/15 text-rose-300 border-rose-900',
    dot: 'bg-rose-500',
    tab: 'data-[active=true]:bg-rose-600 data-[active=true]:text-white data-[active=true]:border-rose-600',
  },
  warning: {
    icon: EyeIcon,
    chip: 'bg-amber-500/10 text-amber-800 border-amber-200',
    chipDark: 'bg-amber-500/15 text-amber-300 border-amber-900',
    dot: 'bg-amber-500',
    tab: 'data-[active=true]:bg-amber-500 data-[active=true]:text-white data-[active=true]:border-amber-500',
  },
  opportunity: {
    icon: RocketLaunchIcon,
    chip: 'bg-emerald-500/10 text-emerald-800 border-emerald-200',
    chipDark: 'bg-emerald-500/15 text-emerald-300 border-emerald-900',
    dot: 'bg-emerald-500',
    tab: 'data-[active=true]:bg-emerald-600 data-[active=true]:text-white data-[active=true]:border-emerald-600',
  },
};

const PREVIEW_COUNT = 5;

export function AttentionChip({ issue, isDarkMode, compact = false }) {
  const tone = TONES[issue.severity] || TONES.warning;
  return (
    <span
      title={issue.detail}
      className={`inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap ${
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[11px]'
      } ${isDarkMode ? tone.chipDark : tone.chip}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      {issue.title}
    </span>
  );
}

/**
 * "Needs attention" command strip for the Stores hub.
 * `activeFilter` also filters the table below (null = off).
 */
export default function StoreAttentionCenter({
  attention,
  loading,
  activeFilter,
  onFilterChange,
  onOpenStore,
  isDarkMode,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [listSeverity, setListSeverity] = useState(null);

  const { counts, atRiskSpend, atRiskStores, byGroup } = attention;
  const totalFlagged = counts.critical + counts.warning + counts.opportunity;

  // Default list: most urgent bucket that has stores.
  const effectiveSeverity = listSeverity
    || (counts.critical ? 'critical' : counts.warning ? 'warning' : counts.opportunity ? 'opportunity' : null);

  const items = useMemo(() => {
    const all = [...byGroup.values()].filter((i) => !effectiveSeverity || i.severity === effectiveSeverity);
    all.sort((a, b) => b.score - a.score);
    return all;
  }, [byGroup, effectiveSeverity]);

  const shown = showAll ? items : items.slice(0, PREVIEW_COUNT);

  const shell = isDarkMode
    ? 'border-slate-700 bg-gradient-to-br from-slate-900 to-slate-900/60'
    : 'border-slate-200 bg-gradient-to-br from-white to-orange-50/40';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = isDarkMode ? 'text-white' : 'text-slate-900';

  if (loading && !totalFlagged) {
    return (
      <div className={`mb-5 rounded-xl border px-4 py-4 animate-pulse ${shell}`} aria-busy="true">
        <div className={`h-4 w-48 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />
        <div className={`mt-3 h-3 w-80 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`} />
      </div>
    );
  }

  if (!loading && !totalFlagged) {
    return (
      <div className={`mb-5 rounded-xl border px-4 py-3 flex items-center gap-2 ${shell}`}>
        <SparklesIcon className="h-5 w-5 text-emerald-500" />
        <p className={`text-sm font-medium ${strong}`}>All clear</p>
        <p className={`text-sm ${muted}`}>No store needs urgent attention in the last 30 days.</p>
      </div>
    );
  }

  const tabs = ['critical', 'warning', 'opportunity'];

  return (
    <section className={`mb-5 rounded-xl border overflow-hidden ${shell}`} aria-label="Stores needing attention">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-600 text-white shrink-0">
            <BoltIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className={`text-sm font-bold ${strong}`}>
              {counts.critical > 0
                ? `${counts.critical} store${counts.critical === 1 ? '' : 's'} need action today`
                : `${totalFlagged} store${totalFlagged === 1 ? '' : 's'} worth a look`}
            </h2>
            <p className={`text-xs ${muted}`}>
              {atRiskSpend > 0
                ? `${formatAttentionMoney(atRiskSpend)} of ad spend is not paying back across ${atRiskStores} store${atRiskStores === 1 ? '' : 's'} (last 30 days)`
                : 'Based on the last 30 days vs the 30 days before'}
            </p>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {tabs.map((sev) => {
            const tone = TONES[sev];
            const Icon = tone.icon;
            const active = effectiveSeverity === sev;
            return (
              <button
                key={sev}
                type="button"
                data-active={active}
                disabled={!counts[sev]}
                onClick={() => { setListSeverity(sev); setShowAll(false); setExpanded(true); }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${tone.tab} ${
                  isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {ATTENTION_SEVERITY[sev].label}
                <span className="tabular-nums">{counts[sev]}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={`p-1.5 rounded-md ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
          </button>
        </div>
      </div>

      {expanded && effectiveSeverity && (
        <div className={`border-t ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
          <ul>
            {shown.map(({ group, store, issues, spend }) => (
              <li
                key={group.id}
                className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 border-b last:border-b-0 ${
                  isDarkMode ? 'border-slate-800 hover:bg-slate-800/40' : 'border-slate-100 hover:bg-white/80'
                }`}
              >
                <div className="min-w-[10rem] flex-1">
                  <p className={`text-sm font-semibold truncate ${strong}`}>{group.name}</p>
                  <p className={`text-[11px] ${muted}`}>{formatAttentionMoney(spend)} spend · {store.region}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 flex-[2] min-w-[14rem]">
                  {issues.slice(0, 2).map((issue) => (
                    <div key={issue.key} className="flex items-center gap-1.5">
                      <AttentionChip issue={issue} isDarkMode={isDarkMode} />
                      <span className={`text-[11px] ${muted}`}>{issue.detail}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onOpenStore(store)}
                  className="inline-flex items-center gap-0.5 rounded-md bg-orange-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
                >
                  Open
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className={`flex flex-wrap items-center gap-3 px-4 py-2 ${isDarkMode ? 'bg-slate-900/50' : 'bg-slate-50/70'}`}>
            {items.length > PREVIEW_COUNT && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-semibold text-orange-600 hover:underline"
              >
                {showAll ? 'Show fewer' : `Show all ${items.length}`}
              </button>
            )}
            <button
              type="button"
              onClick={() => onFilterChange(activeFilter === effectiveSeverity ? null : effectiveSeverity)}
              className={`text-xs font-semibold hover:underline ${activeFilter === effectiveSeverity ? 'text-rose-600' : 'text-orange-600'}`}
            >
              {activeFilter === effectiveSeverity
                ? 'Clear table filter'
                : `Filter the table to these ${items.length}`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
